import { useEffect, useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, RefreshCw, Trash2, ExternalLink } from "lucide-react";

export default function AdminInstagramSync() {
  const [token, setToken] = useState("");
  const [tokenInfo, setTokenInfo] = useState({ has_token: false });
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadInfo = async () => {
    const { data: t } = await axios.get(`${API}/admin/instagram-token`);
    setTokenInfo(t);
    const { data: p } = await axios.get(`${API}/instagram-posts`);
    setPosts(p);
  };
  useEffect(() => { loadInfo(); }, []);

  const sync = async () => {
    if (!token && !tokenInfo.has_token) {
      toast.error("Paste your Instagram access token first");
      return;
    }
    setBusy(true);
    try {
      const body = token ? { access_token: token, limit: 12 } : { access_token: "REUSE_STORED_" + "x".repeat(20), limit: 12 };
      // If user didn't paste a new token, we still need the actual one — the backend requires it.
      // Re-fetch via getFullToken (not exposed) — for simplicity, ask the admin to paste it each sync unless kept in browser storage.
      const { data } = await axios.post(`${API}/admin/instagram-sync`, {
        access_token: token || "",
        limit: 12,
      });
      toast.success(`Synced ${data.inserted + data.updated} of ${data.total} posts (${data.inserted} new, ${data.updated} updated)`);
      setToken("");
      loadInfo();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    await axios.delete(`${API}/admin/instagram-posts/${p.id}`);
    toast.success("Deleted");
    loadInfo();
  };

  return (
    <div className="fade-in" data-testid="admin-instagram-page">
      <div className="mb-10">
        <div className="font-micro text-[#8A8680] mb-3 flex items-center gap-2"><Instagram size={12} /> Meta Graph API</div>
        <h1 className="font-serif text-[#EDE4D3] text-5xl">Instagram Sync.</h1>
        <p className="text-[#8A8680] mt-3 max-w-2xl">Pulls the latest posts from your @theteakstudio_ account and refreshes the home-page grid. Requires an Instagram Graph API long-lived access token — a one-time setup.</p>
      </div>

      <div className="grid md:grid-cols-[1fr_1.2fr] gap-8">
        <div className="p-6" style={{ background: "#141414", border: "1px solid var(--hairline)" }} data-testid="ig-sync-card">
          <div className="font-micro text-[#B8895C] mb-3">Access Token</div>
          <div className="font-micro text-[#8A8680] mb-4">
            {tokenInfo.has_token ? `Stored: ${tokenInfo.masked}` : "No token stored yet."}
          </div>
          <input
            type="password"
            className="input-teak"
            placeholder="Paste new access token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="ig-token-input"
          />
          <button
            onClick={sync}
            disabled={busy || (!token && !tokenInfo.has_token)}
            className="btn-bronze w-full mt-6"
            style={(busy || (!token && !tokenInfo.has_token)) ? { opacity: 0.4, cursor: "not-allowed" } : {}}
            data-testid="ig-sync-btn"
          >
            <RefreshCw size={12} className={`inline mr-2 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Syncing…" : "Sync Now"}
          </button>

          <div className="mt-8 pt-6 space-y-3 text-sm text-[#8A8680]" style={{ borderTop: "1px solid var(--hairline)" }}>
            <div className="font-micro text-[#EDE4D3]">Setup (one-time · ~10 min)</div>
            <ol className="list-decimal list-inside space-y-2 leading-relaxed">
              <li>Convert your @theteakstudio_ Instagram account to a <b>Business</b> or <b>Creator</b> account.</li>
              <li>Create a Facebook Page and link it to that Instagram account.</li>
              <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-[#B8895C] link-underline">developers.facebook.com/apps</a> and create a new app (type: Business).</li>
              <li>Add the <b>Instagram Graph API</b> product to the app.</li>
              <li>In Graph API Explorer, generate a User Access Token with <code className="text-[#B8895C]">instagram_basic</code> + <code className="text-[#B8895C]">pages_show_list</code> permissions.</li>
              <li>Exchange it for a <b>long-lived token</b> (60-day) via <code className="text-[#B8895C]">GET /oauth/access_token?grant_type=ig_exchange_token</code>.</li>
              <li>Paste the long-lived token here and click <b>Sync Now</b>.</li>
            </ol>
            <a href="https://developers.facebook.com/docs/instagram-basic-display-api/getting-started" target="_blank" rel="noreferrer" className="font-micro text-[#B8895C] link-underline inline-flex items-center gap-1 mt-2">
              Full instructions <ExternalLink size={11} />
            </a>
          </div>
        </div>

        <div>
          <div className="font-micro text-[#8A8680] mb-4">Current posts on the home page ({posts.length})</div>
          <div className="grid grid-cols-3 gap-3" data-testid="ig-posts-grid">
            {posts.map((p) => (
              <div key={p.id} className="relative aspect-square" style={{ background: "#141414", border: "1px solid var(--hairline)" }} data-testid={`ig-post-${p.id}`}>
                <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                {p.is_reel && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#0B0B0C]/80 font-micro text-[9px] text-[#EDE4D3]">REEL</div>
                )}
                <button
                  onClick={() => del(p)}
                  className="absolute bottom-2 right-2 w-6 h-6 flex items-center justify-center bg-[#0B0B0C]/80 text-[#EDE4D3] hover:text-[#B8895C]"
                  data-testid={`ig-post-del-${p.id}`}
                >
                  <Trash2 size={11} strokeWidth={1.25} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
